import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, Paper,
  Alert, CircularProgress, Collapse, Divider,
} from '@mui/material';
import HowToRegIcon from '@mui/icons-material/HowToReg';

const API = process.env.REACT_APP_API_URL || '';

// ── Paleta de producto ─────────────────────────────────────────────────────────
const C = {
  fondo:   '#17192B',
  card:    '#202A46',
  input:   '#162239',
  acento:  '#34d399',
  cta:     '#1e56d4',
  texto:   '#edf2f7',
  muted:   '#364f6b',
  borde:   'rgba(255,255,255,0.075)',
};

// Umbral único para la experiencia móvil del paciente (Login + Portal). No usar
// xs/sm de MUI (600px) ni theme.breakpoints.down('md') (900px) acá: 768px es el
// valor pedido explícitamente para esta adaptación.
const MOBILE_MQ = '@media (max-width:768px)';
const HOVER_MQ = '@media (hover: hover)';

const CARD_SX = {
  background: C.card,
  backgroundImage: 'none',
  p: { xs: '22px', sm: '28px' },
  borderRadius: '14px',
  width: '100%',
  maxWidth: 328,
  border: '1px solid rgba(255,255,255,0.065)',
  boxShadow: '0 4px 32px rgba(0,0,0,0.55)',
  mx: 2,
  // Bug preexistente: sin box-sizing:border-box, el padding se suma por fuera
  // de maxWidth (328 + 44 de padding + 2 de borde = 374px reales), lo que
  // desborda en anchos angostos como 360px. Se corrige solo por debajo de
  // 768px para no alterar el tamaño de la tarjeta en escritorio.
  [MOBILE_MQ]: { boxSizing: 'border-box' },
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 42,
    borderRadius: '8px',
    background: C.input,
    fontSize: 13.5,
    [MOBILE_MQ]: { height: 44 },
    '& fieldset': {
      border: `1px solid ${C.borde}`,
      transition: 'border 0.15s, box-shadow 0.15s',
    },
    [HOVER_MQ]: { '&:hover fieldset': { border: '1px solid rgba(255,255,255,0.13)' } },
    '&.Mui-focused fieldset': {
      border: '1px solid rgba(52,211,153,0.4)',
      boxShadow: '0 0 0 3px rgba(52,211,153,0.07)',
    },
  },
  '& .MuiInputBase-input': { color: '#9fb3c8', py: '11px' },
  '& .MuiInputBase-input::placeholder': { color: '#6b8aaa', opacity: 1 },
};

const btnSx = {
  background: C.cta,
  color: '#fff',
  height: 48,
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: '1.2px',
  borderRadius: '8px',
  boxShadow: '0 2px 12px rgba(30,86,212,0.35)',
  textTransform: 'uppercase',
  touchAction: 'manipulation',
  [HOVER_MQ]: { '&:hover': { background: '#2460e0', boxShadow: '0 4px 16px rgba(30,86,212,0.45)' } },
  '&.Mui-disabled': { opacity: 0.45, color: '#fff', background: C.cta },
};

const FADE_IN = {
  '@keyframes lFadeIn': {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to:   { opacity: 1, transform: 'translateY(0)' },
  },
  animation: 'lFadeIn 0.22s ease-out',
};

// ── Isotipo: cruz de 5 bloques ─────────────────────────────────────────────────
function Isotipo() {
  return (
    <Box sx={{
      width: 42, height: 42, borderRadius: '11px',
      background: '#0d2245',
      border: '1px solid rgba(52,211,153,0.22)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <rect x="9"  y="2"  width="6" height="6" rx="1.5" fill="#34d399"/>
        <rect x="2"  y="9"  width="6" height="6" rx="1.5" fill="#34d399"/>
        <rect x="9"  y="9"  width="6" height="6" rx="1.5" fill="#34d399"/>
        <rect x="16" y="9"  width="6" height="6" rx="1.5" fill="#34d399"/>
        <rect x="9"  y="16" width="6" height="6" rx="1.5" fill="#34d399"/>
      </svg>
    </Box>
  );
}

// ── Pill tab selector ──────────────────────────────────────────────────────────
function PillTabs({ tab, onChange }) {
  return (
    <Box sx={{ background: C.input, borderRadius: '9px', p: '3px', display: 'flex', mb: '20px' }}>
      {[{ label: 'Ingresar', val: 0 }, { label: 'Registrarme', val: 1 }].map(({ label, val }) => (
        <Box key={val} onClick={() => onChange(val)} sx={{
          flex: 1, height: 34, borderRadius: '7px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', fontSize: 13,
          fontWeight: tab === val ? 600 : 400,
          color: tab === val ? '#d8eaff' : C.muted,
          background: tab === val ? '#2b4880' : 'transparent',
          boxShadow: tab === val ? '0 1px 6px rgba(0,0,0,0.5)' : 'none',
          transition: 'all 0.15s',
          userSelect: 'none',
          touchAction: 'manipulation',
          [MOBILE_MQ]: { height: 40 },
        }}>
          {label}
        </Box>
      ))}
    </Box>
  );
}

// ── TextField sin label flotante ───────────────────────────────────────────────
function Field({ ph, type = 'text', value, onChange, onKeyPress, autoComplete, name, fullWidth = true, inputRef }) {
  return (
    <TextField
      type={type}
      value={value}
      onChange={onChange}
      onKeyPress={onKeyPress}
      name={name}
      autoComplete={autoComplete}
      placeholder={ph}
      fullWidth={fullWidth}
      inputRef={inputRef}
      inputProps={{ autoComplete }}
      sx={fieldSx}
    />
  );
}

// ── Componente principal ───────────────────────────────────────────────────────
export default function Login({ onLogin }) {
  const passwordRef = useRef(null);
  const [tab, setTab]   = useState(0);
  const [form, setForm] = useState({ nombre: '', apellido: '', dni: '', email: '', password: '', telefono: '' });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);

  const [inicio, setInicio] = useState({ nombre_clinica: '', subtitulo: '', logo_base64: '' });
  useEffect(() => {
    axios.get(`${API}/api/configuracion/inicio`).then(r => setInicio(r.data)).catch(() => {});
  }, []);

  const [verificandoDni, setVerificandoDni]       = useState(false);
  const [estadoDni, setEstadoDni]                 = useState(null);
  const [nombrePreRegistrado, setNombrePreRegistrado] = useState('');

  const [recuperando, setRecuperando] = useState(false);
  const [formRec, setFormRec] = useState({ dni: '', email: '', nueva_password: '', confirmar: '' });
  const [exitoRec, setExitoRec] = useState('');

  function guardarSesion(data) {
    sessionStorage.setItem('token', data.token);
    sessionStorage.setItem('rol', data.rol);
    sessionStorage.setItem('nombre', data.nombre);
    sessionStorage.setItem('id', String(data.id || ''));
    sessionStorage.setItem('expira', String(Date.now() + 8 * 60 * 60 * 1000));
    sessionStorage.setItem('datos_confirmados', data.datos_confirmados ? '1' : '0');
    sessionStorage.setItem('medico_id', String(data.medico_id || ''));
    sessionStorage.setItem('debe_cambiar_password', data.debe_cambiar_password ? '1' : '0');
    ['token','rol','nombre','expira','datos_confirmados'].forEach(k => localStorage.removeItem(k));
    onLogin(data);
  }

  async function handleLogin() {
    setLoading(true); setError('');
    try {
      const r = await axios.post(`${API}/api/auth/login`, { email: form.email, password: form.password });
      guardarSesion(r.data);
    } catch (err) { setError(err.response?.data?.error || 'Error al iniciar sesión'); }
    finally { setLoading(false); }
  }

  async function verificarDni(dni) {
    if (!dni || dni.length < 6) { setEstadoDni(null); return; }
    setVerificandoDni(true);
    try {
      const r = await axios.get(`${API}/api/auth/verificar-dni?dni=${encodeURIComponent(dni)}`);
      if (!r.data.existe)                { setEstadoDni('libre'); }
      else if (r.data.pre_registrado)    { setEstadoDni('pre_registrado'); setNombrePreRegistrado(r.data.nombre || ''); }
      else                               { setEstadoDni('ocupado'); }
    } catch { setEstadoDni('libre'); }
    finally { setVerificandoDni(false); }
  }

  async function handleRegistro() {
    setLoading(true); setError('');
    try {
      const r = await axios.post(`${API}/api/auth/registro`, form);
      guardarSesion(r.data);
    } catch (err) { setError(err.response?.data?.error || 'Error al registrarse'); }
    finally { setLoading(false); }
  }

  async function handleActivar() {
    if (!form.password) { setError('Ingresá una contraseña.'); return; }
    setLoading(true); setError('');
    try {
      const r = await axios.post(`${API}/api/auth/activar`, {
        dni: form.dni, email: form.email || undefined, password: form.password,
      });
      guardarSesion(r.data);
    } catch (err) { setError(err.response?.data?.error || 'Error al activar la cuenta'); }
    finally { setLoading(false); }
  }

  function resetTab(v) {
    setTab(v); setError(''); setEstadoDni(null); setNombrePreRegistrado('');
    setRecuperando(false); setExitoRec('');
    setFormRec({ dni: '', email: '', nueva_password: '', confirmar: '' });
    setForm({ nombre: '', apellido: '', dni: '', email: '', password: '', telefono: '' });
  }

  async function handleRecuperar() {
    if (formRec.nueva_password !== formRec.confirmar) { setError('Las contraseñas no coinciden'); return; }
    if (formRec.nueva_password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    setLoading(true); setError('');
    try {
      const r = await axios.post(`${API}/api/auth/recuperar`, {
        dni: formRec.dni, email: formRec.email, nueva_password: formRec.nueva_password,
      });
      setExitoRec(`¡Listo, ${r.data.nombre}! Tu contraseña fue actualizada. Ya podés ingresar.`);
    } catch (err) { setError(err.response?.data?.error || 'Error al recuperar la contraseña'); }
    finally { setLoading(false); }
  }

  const esPreRegistrado    = estadoDni === 'pre_registrado';
  const mostrarFormCompleto = estadoDni === 'libre' || esPreRegistrado;
  const f = field => e => setForm(p => ({ ...p, [field]: e.target.value }));
  const fr = field => e => setFormRec(p => ({ ...p, [field]: e.target.value }));

  return (
    <Box sx={{
      minHeight: '100vh',
      background: C.fondo,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center',
      pt: 'clamp(48px, 10vh, 140px)',
      pb: '48px',
    }}>
      <Paper sx={CARD_SX} elevation={0}>

        {/* ── Identity ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
          {inicio.logo_base64
            ? <img src={inicio.logo_base64} alt="logo"
                style={{ width: 42, height: 42, objectFit: 'contain', borderRadius: 11, flexShrink: 0 }} />
            : <Isotipo />
          }
          <Box>
            <Typography sx={{ color: C.texto, fontSize: 20, fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.15 }}>
              {inicio.nombre_clinica || 'Gestar'}
            </Typography>
            <Typography sx={{ fontSize: 10, mt: '3px', color: C.acento, opacity: 0.72, letterSpacing: '0.2px' }}>
              {inicio.subtitulo || 'Consultorios · Agenda · Historia clínica'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.055)', mb: '18px' }} />

        {/* ── Pill tabs ── */}
        <PillTabs tab={tab} onChange={resetTab} />

        {/* ── Error global ── */}
        <Collapse in={Boolean(error)}>
          <Alert severity="error" sx={{ mb: '12px', fontSize: 12.5 }}>{error}</Alert>
        </Collapse>

        {/* ══ LOGIN ══ */}
        {tab === 0 && !recuperando && (
          <Box key="login" sx={{ display: 'flex', flexDirection: 'column', ...FADE_IN }}>
            <Box component="form" onSubmit={e => { e.preventDefault(); handleLogin(); }}
              sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
              <Field ph="Email o DNI" value={form.email} name="username" autoComplete="username" onChange={f('email')}
                onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); passwordRef.current?.focus(); } }} />
              <Field ph="Contraseña" type="password" value={form.password} name="password" autoComplete="current-password" onChange={f('password')}
                inputRef={passwordRef} />
              <Button type="submit" variant="contained" fullWidth disabled={loading} sx={{ ...btnSx, mt: '6px' }}>
                {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Ingresar'}
              </Button>
            </Box>
            <Button variant="text" size="small"
              onClick={() => { setRecuperando(true); setError(''); setExitoRec(''); }}
              sx={{ color: C.muted, fontSize: 11.5, mt: '14px', textTransform: 'none', alignSelf: 'center',
                touchAction: 'manipulation',
                [HOVER_MQ]: { '&:hover': { color: '#90caf9', bgcolor: 'transparent' } } }}>
              ¿Olvidaste tu contraseña?
            </Button>
          </Box>
        )}

        {/* ══ RECUPERAR CONTRASEÑA ══ */}
        {tab === 0 && recuperando && (
          <Box key="recuperar" sx={{ display: 'flex', flexDirection: 'column', gap: '9px', ...FADE_IN }}>
            <Box sx={{ mb: '4px' }}>
              <Typography sx={{ color: C.acento, fontWeight: 700, fontSize: 13.5, mb: '3px' }}>
                Recuperar contraseña
              </Typography>
              <Typography sx={{ color: C.muted, fontSize: 12 }}>
                Ingresá tu DNI y email para crear una nueva contraseña.
              </Typography>
            </Box>
            {exitoRec ? (
              <>
                <Alert severity="success" sx={{ fontSize: 12.5 }}>{exitoRec}</Alert>
                <Button variant="contained" fullWidth
                  onClick={() => { setRecuperando(false); setExitoRec(''); setError(''); }}
                  sx={{ ...btnSx, mt: '4px' }}>
                  Ir a Ingresar
                </Button>
              </>
            ) : (
              <>
                <Field ph="DNI"               value={formRec.dni}           onChange={fr('dni')} />
                <Field ph="Email" type="email" value={formRec.email}         onChange={fr('email')} />
                <Field ph="Nueva contraseña"  type="password" value={formRec.nueva_password} onChange={fr('nueva_password')} />
                <Field ph="Confirmar contraseña" type="password" value={formRec.confirmar}  onChange={fr('confirmar')}
                  onKeyPress={e => e.key === 'Enter' && handleRecuperar()} />
                <Button variant="contained" fullWidth disabled={loading} onClick={handleRecuperar} sx={{ ...btnSx, mt: '4px' }}>
                  {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Cambiar contraseña'}
                </Button>
                <Button variant="text" size="small" onClick={() => { setRecuperando(false); setError(''); }}
                  sx={{ color: C.muted, fontSize: 11.5, textTransform: 'none', alignSelf: 'center',
                    touchAction: 'manipulation',
                    [HOVER_MQ]: { '&:hover': { color: '#90caf9', bgcolor: 'transparent' } } }}>
                  ← Volver a ingresar
                </Button>
              </>
            )}
          </Box>
        )}

        {/* ══ REGISTRO ══ */}
        {tab === 1 && (
          <Box key="registro" sx={{ display: 'flex', flexDirection: 'column', ...FADE_IN }}>

            <Field ph="DNI *" value={form.dni}
              onChange={e => { setForm(p => ({ ...p, dni: e.target.value })); setEstadoDni(null); }}
              onKeyPress={e => e.key === 'Enter' && verificarDni(form.dni)} />

            <Box sx={{ mt: '8px', mb: '4px', minHeight: 18, pl: '2px' }}>
              {verificandoDni && <Typography sx={{ fontSize: 11.5, color: C.acento }}>Verificando...</Typography>}
              {esPreRegistrado && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <HowToRegIcon sx={{ fontSize: 13, color: '#66bb6a' }} />
                  <Typography sx={{ fontSize: 11.5, color: '#66bb6a' }}>
                    ¡Hola {nombrePreRegistrado}! Solo elegí una contraseña.
                  </Typography>
                </Box>
              )}
              {estadoDni === 'ocupado' && (
                <Typography sx={{ fontSize: 11.5, color: '#f44336' }}>
                  Este DNI ya tiene cuenta. Ingresá desde "Ingresar".
                </Typography>
              )}
              {estadoDni === null && !verificandoDni && (
                <Typography sx={{ fontSize: 11.5, color: C.muted }}>Ingresá tu DNI y presioná Continuar</Typography>
              )}
            </Box>

            <Collapse in={estadoDni === null} timeout={220} unmountOnExit>
              <Button variant="outlined" fullWidth
                disabled={verificandoDni || form.dni.length < 6}
                onClick={() => verificarDni(form.dni)}
                sx={{
                  mt: '4px', height: 42, fontSize: 13.5, fontWeight: 600, borderRadius: '8px',
                  color: '#90caf9', borderColor: 'rgba(144,202,249,0.28)',
                  textTransform: 'none',
                  touchAction: 'manipulation',
                  [MOBILE_MQ]: { height: 44 },
                  [HOVER_MQ]: { '&:hover': { borderColor: '#90caf9', bgcolor: 'rgba(144,202,249,0.06)' } },
                  '&.Mui-disabled': { opacity: 0.4, color: C.muted, borderColor: C.borde },
                }}>
                {verificandoDni ? 'Verificando...' : 'Continuar'}
              </Button>
            </Collapse>

            <Collapse in={estadoDni === 'libre'} timeout={280} unmountOnExit>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px', mt: '10px' }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: '9px', [MOBILE_MQ]: { flexDirection: 'column' } }}>
                  <Field ph="Nombre *"   value={form.nombre}   onChange={f('nombre')} />
                  <Field ph="Apellido *" value={form.apellido} onChange={f('apellido')} />
                </Box>
                <Field ph="Teléfono" value={form.telefono} onChange={f('telefono')} />
              </Box>
            </Collapse>

            <Collapse in={mostrarFormCompleto} timeout={300} unmountOnExit>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px', mt: '10px' }}>
                <Field ph={esPreRegistrado ? 'Email (opcional)' : 'Email *'} type="email"
                  value={form.email} onChange={f('email')} />
                {esPreRegistrado && (
                  <Typography sx={{ fontSize: 11.5, color: C.muted, mt: '-2px' }}>
                    Podés dejarlo en blanco si ya lo tenemos.
                  </Typography>
                )}
                <Field ph="Contraseña *" type="password" value={form.password} onChange={f('password')}
                  onKeyPress={e => e.key === 'Enter' && (esPreRegistrado ? handleActivar() : handleRegistro())} />
                <Button variant="contained" fullWidth disabled={loading}
                  onClick={esPreRegistrado ? handleActivar : handleRegistro}
                  sx={{ ...btnSx, mt: '4px' }}>
                  {loading
                    ? <CircularProgress size={20} sx={{ color: '#fff' }} />
                    : esPreRegistrado ? 'Activar mi cuenta' : 'Crear cuenta'}
                </Button>
              </Box>
            </Collapse>

          </Box>
        )}
      </Paper>
    </Box>
  );
}
